import React, { useState } from 'react';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import BlockThemeProvider from '@components/blocks/BlockThemeProvider';
import { LoadingIcon } from '@cvent/carina/components/LoadingIcon';
import Button from '@cvent/carina/components/Button';
import { useTranslate } from 'nucleus-text';
import { CSSObject } from '@emotion/react';
import { useThemeValues } from '@context/ThemeProvider';
import Typography from '@cvent/blocks/components/Typography';
import Image from './Image';

enum DisplayStyleOption {
  center = 'center',
  start = 'start'
}
interface EmptyContentStateProps {
  /** Displaying elements aligned by center or start */
  displayStyle?: DisplayStyleOption;
  /** Placeholder image */
  image?: React.ReactNode;
  /** Display image below the text */
  imageBottom?: boolean;
  imageAlt?: string;
  disableImageAutosizing?: boolean;
  /** title of the empty content */
  title: string;
  /** subtitle of the empty content */
  subtitle?: React.ReactNode;
  /* TestId */
  testID: string;
  /** renders refresh button that when clicked reloads the browser */
  showRefreshButton?: boolean;
  hideIllustrationsPaddingTop?: number;
  /** Remove the default margin-top  set to the image element  */
  hideImageMarginTop?: boolean;
  hasLargeTitle?: boolean;
  hasCustomParagraphWidth?: boolean;
  hasSmallTitleMargin?: boolean;
}

const useStyles = (
  displayStyle,
  disableImageAutosizing,
  hideImageMarginTop,
  hasLargeTitle,
  hasCustomParagraphWidth,
  hasSmallTitleMargin
): Record<string, CSSObject> => {
  const { font } = useTheme();
  const { isMobile, isMobileOrTablet, mediaIsMobile } = useBreakpoints();

  const titleColor = font.color.base;

  let imageWidth;
  if (disableImageAutosizing) {
    imageWidth = 'unset';
  } else if (!isMobileOrTablet) {
    imageWidth = '70%';
  } else if (isMobile) {
    imageWidth = '30%';
  } else {
    imageWidth = '50%';
  }

  const styles = {
    center: {
      container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto'
      },
      image: {
        width: imageWidth,
        maxWidth: disableImageAutosizing ? 'none' : 375,
        height: 'auto',
        marginBottom: 16,
        ...(!hideImageMarginTop && { marginTop: isMobile ? 15 : 30 })
      },
      title: {
        color: titleColor,
        marginBottom: hasSmallTitleMargin ? 8 : 16
      },
      subtitle: {
        display: 'flex',
        justifyContent: 'center',
        fontSize: font.base.size['5'],
        fontWeight: font.mono.weight.regular,
        color: titleColor,
        marginLeft: 8,
        marginRight: 8,
        width: hasCustomParagraphWidth ? undefined : 300,
        [mediaIsMobile]: {
          width: '100%'
        }
      },
      buttonContainer: {
        marginTop: 16
      }
    },
    start: {
      container: {
        display: 'flex'
      },
      imageContainer: {
        alignItems: 'center',
        textAlign: 'center'
      },
      textContainer: {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'start'
      },
      title: {
        fontSize: font.base.size.l,
        color: titleColor,
        fontFamily: font.base.family,
        fontWeight: font.base.weight.light
      },
      subtitle: {
        fontSize: font.base.size.m,
        color: titleColor,
        fontFamily: font.base.family,
        fontWeight: font.base.weight.light,
        width: 300
      },
      buttonContainer: {
        marginTop: 16
      }
    }
  };

  return styles[displayStyle];
};

function EmptyContentState({
  displayStyle = DisplayStyleOption.center,
  image,
  imageBottom,
  title,
  subtitle,
  testID,
  imageAlt,
  disableImageAutosizing,
  showRefreshButton,
  hideIllustrationsPaddingTop = 175,
  hideImageMarginTop = false,
  hasLargeTitle = false,
  hasCustomParagraphWidth = false,
  hasSmallTitleMargin = false
}: EmptyContentStateProps): JSX.Element {
  const styles = useStyles(
    displayStyle,
    disableImageAutosizing,
    hideImageMarginTop,
    hasLargeTitle,
    hasCustomParagraphWidth,
    hasSmallTitleMargin
  );
  const [pageIsReloading, setPageIsReloading] = useState(false);
  const { translate } = useTranslate();

  const { hideIllustrations } = useThemeValues();

  let imageContainer;
  if (hideIllustrations) {
    imageContainer = <div css={{ paddingTop: hideIllustrationsPaddingTop }} />;
  } else if (image) {
    imageContainer =
      typeof image === 'string' ? (
        <Image src={image} style={styles.image} testID={`${testID}-image`} alt={imageAlt} />
      ) : (
        <div css={styles.image} {...injectTestId(`${testID}-image`)}>
          {image}
        </div>
      );
  }

  if (displayStyle === DisplayStyleOption.start) {
    return (
      <div css={styles.container} {...injectTestId(testID)}>
        {imageContainer && !imageBottom && <div css={styles.imageContainer}>{imageContainer}</div>}
        <div css={styles.textContainer}>
          {title && (
            <span {...injectTestId(`${testID}-title`)} css={styles.title} tabIndex={0}>
              {title}
            </span>
          )}
          {title && subtitle && <div style={{ height: 8 }} />}
          {subtitle && (
            <span {...injectTestId(`${testID}-subtitle`)} css={styles.subtitle} tabIndex={0}>
              {subtitle}
            </span>
          )}
          {imageContainer && imageBottom && <div css={styles.imageContainer}>{imageContainer}</div>}
        </div>
      </div>
    );
  }

  return (
    <div css={styles.container} {...injectTestId(testID)}>
      {!imageBottom && imageContainer}
      {title &&
        (hasLargeTitle ? (
          <Typography as="h2" css={styles.title} testID={`${testID}-title`}>
            {title}
          </Typography>
        ) : (
          <span {...injectTestId(`${testID}-title`)} css={styles.title} tabIndex={0}>
            {title}
          </span>
        ))}
      {subtitle && (
        <span {...injectTestId(`${testID}-subtitle`)} css={styles.subtitle} tabIndex={0}>
          {subtitle}
        </span>
      )}
      {imageBottom && imageContainer}
      {showRefreshButton && (
        <div css={styles.buttonContainer}>
          <Button
            appearance="filled"
            onClick={() => {
              window.location.reload();
              setPageIsReloading(true);
            }}
            text={translate('Universal-Error-Refresh-Button')}
            icon={pageIsReloading ? LoadingIcon : undefined}
            disabled={pageIsReloading}
          />
        </div>
      )}
    </div>
  );
}

export function EmptyContentStateWithBlocks(props: EmptyContentStateProps): JSX.Element {
  return (
    <BlockThemeProvider blockColorKey="background">
      <EmptyContentState {...props} />
    </BlockThemeProvider>
  );
}

export default EmptyContentState;
